import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import Table from "../../components/common/Table";
import Modal from "../../components/common/Modal";
import {
  KeyIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import BrandForm from "../../components/forms/BrandForm";
import { useBrandsInfinite } from "../../hooks/useBrandsInfinite";
import { useDebounce } from "../../hooks/useDebounce";
import {
  Brand,
  BrandCreateFormData,
  BrandUpdateFormData,
} from "../../schemas/brandSchema";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { getChangedFields } from "../../utils/formUtils";

const BrandList: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const {
    brands,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createBrand,
    updateBrand,
    deleteBrands,
    resetBrandPassword,
  } = useBrandsInfinite({ search: debouncedSearch || undefined });

  const { canEdit, canDelete, canCreate, canView } =
    useAuthPermissions("brands");

  const handleEdit = (brand: Brand) => {
    setSelectedBrand(brand);
    setIsModalOpen(true);
  };

  const handleDelete = (brand: Brand) => {
    // Creatives keep their brand link on delete, so say what actually happens.
    const suffix =
      brand.creativeCount > 0
        ? `\n\n${brand.creativeCount} creative(s) are assigned to this brand. They will stay assigned but the login will stop working.`
        : "";
    if (window.confirm(`Delete the brand "${brand.name}"?${suffix}`)) {
      deleteBrands([brand.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (window.confirm(`Delete ${ids.length} brand(s)?`)) {
      deleteBrands(ids);
      setRowSelection({});
    }
  };

  const handleResetPassword = (brand: Brand) => {
    if (
      window.confirm(
        `Generate a new password for "${brand.name}" and email it to ${brand.email}?\n\nTheir current password will stop working immediately.`,
      )
    ) {
      resetBrandPassword(brand.id);
    }
  };

  const handleSubmit = (data: BrandCreateFormData | BrandUpdateFormData) => {
    const closeModal = () => {
      setIsModalOpen(false);
      setSelectedBrand(null);
    };

    if (selectedBrand) {
      const changedFields = getChangedFields(data as BrandUpdateFormData, {
        name: selectedBrand.name,
        email: selectedBrand.email,
        phoneNumber: selectedBrand.phoneNumber || "",
      });
      updateBrand(
        { id: selectedBrand.id, brand: changedFields },
        { onSuccess: closeModal },
      );
    } else {
      createBrand(data as BrandCreateFormData, { onSuccess: closeModal });
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedBrand(null);
  };

  const columns: ColumnDef<Brand>[] = [
    { accessorKey: "name", header: "Brand" },
    { accessorKey: "username", header: "Username" },
    { accessorKey: "email", header: "Email" },
    { accessorKey: "phoneNumber", header: "Phone Number" },
    {
      accessorKey: "creativeCount",
      header: "Creatives",
      cell: ({ row }) => row.original.creativeCount ?? 0,
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            row.original.isActive
              ? "bg-success-100 text-success-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {row.original.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
      visible: canEdit,
    },
    {
      label: "Resend Password",
      icon: <KeyIcon className="h-4 w-4" />,
      onClick: handleResetPassword,
      variant: "warning" as const,
      visible: canEdit,
      tooltip: "Generate a new password and email it to the brand",
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  return (
    <div className="flex flex-col px-6 py-2">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Brands</h1>
        {canCreate && (
          <button
            onClick={() => {
              setSelectedBrand(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {canView && (
        <Table
          data={brands}
          columns={columns}
          actions={actions}
          searchable
          isLoading={isLoading}
          maxHeight="70vh"
          infinite
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          onSearch={setSearch}
          selectable={canDelete}
          getRowId={(item) => item.id}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          bulkActions={
            canDelete
              ? [
                  {
                    label: "Delete Selected",
                    icon: <TrashIcon className="h-4 w-4" />,
                    onClick: handleBulkDelete,
                    variant: "danger" as const,
                  },
                ]
              : undefined
          }
        />
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCancel}
        title={selectedBrand ? "Edit Brand" : "Add Brand"}
        size="full"
      >
        <BrandForm
          initialData={selectedBrand || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          isUpdate={!!selectedBrand}
          isOpen={isModalOpen}
        />
      </Modal>
    </div>
  );
};

export default BrandList;
